import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./DashboardStudent.css";
import NavbarAll from "../Navbar/NavbarAll";

// jsPDF's default font is Latin-only, so Urdu/Arabic free text (e.g. the
// "New Lesson" field) renders as invalid characters. Detect such text and
// draw it as an image using the browser's own shaping instead.
const ARABIC_SCRIPT = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
const hasArabicScript = (value) =>
  typeof value === "string" && ARABIC_SCRIPT.test(value);

const renderTextToImage = (text, fontSizePt = 11) => {
  const scale = 4; // render at higher resolution for a crisp PDF
  const fontFamily =
    "'Noto Nastaliq Urdu','Jameel Noori Nastaleeq','Geeza Pro','Segoe UI','Arial Unicode MS',sans-serif";
  const measureCtx = document.createElement("canvas").getContext("2d");
  measureCtx.font = `${fontSizePt}px ${fontFamily}`;
  const textWidth = Math.max(1, Math.ceil(measureCtx.measureText(text).width));
  const widthPt = textWidth + 6;
  const heightPt = Math.ceil(fontSizePt * 1.9);

  const canvas = document.createElement("canvas");
  canvas.width = widthPt * scale;
  canvas.height = heightPt * scale;
  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);
  ctx.font = `${fontSizePt}px ${fontFamily}`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "right";
  ctx.direction = "rtl";
  ctx.fillStyle = "#1c2b22";
  ctx.fillText(text, widthPt - 3, heightPt / 2);

  return { dataUrl: canvas.toDataURL("image/png"), widthPt, heightPt };
};

export default function DashboardStudent({ user: hifzUser, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated =
    !!localStorage.getItem("token") && !!localStorage.getItem("role");

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/hifz-login", { replace: true });
  };
  const [studentId, setStudentId] = useState(
    () =>
      location.state?.student?.uid ||
      location.state?.student?.id ||
      hifzUser?.userId ||
      localStorage.getItem("userId")
  );
  const [student, setStudent] = useState(location.state?.student || null);
  const params = new URLSearchParams(location.search);
  const deepLinkToken = params.get("token");
  const apiURL = process.env.REACT_APP_API_URL;

  const [progressPercent, setProgressPercent] = useState(0);
  const [resolvingStudent, setResolvingStudent] = useState(!!deepLinkToken);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Resolve student from deep link token
  useEffect(() => {
    if (!deepLinkToken) return;
    setResolvingStudent(true);
    axios
      .get(`${apiURL}/students/by-token/${deepLinkToken}`)
      .then((res) => {
        setStudent(res.data);
        setStudentId(res.data?.uid);
      })
      .catch((err) => console.error("Failed to resolve deep link", err))
      .finally(() => setResolvingStudent(false));
  }, [deepLinkToken, apiURL]);

  // When logged in with userId but no student (e.g. after login), fetch profile
  useEffect(() => {
    if (deepLinkToken || !studentId || student) return;
    setResolvingStudent(true);
    const token = localStorage.getItem("token");
    axios
      .get(`${apiURL}/users/${studentId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data;
        setStudent({ ...data, id: data.uid, uid: data.uid });
      })
      .catch((err) => console.error("Failed to fetch user", err))
      .finally(() => setResolvingStudent(false));
  }, [studentId, deepLinkToken, student, apiURL]);

  // Fetch progress for the bar (e.g. attendance this month)
  useEffect(() => {
    if (!studentId && !deepLinkToken) return;
    const fetchProgress = async () => {
      try {
        const now = new Date();
        const month = now.getMonth() + 1;
        const year = now.getFullYear();
        let res;
        if (deepLinkToken) {
          res = await axios.get(
            `${apiURL}/students/by-token/${deepLinkToken}/present-days`,
            { params: { month, year } }
          );
        } else {
          const token = localStorage.getItem("token");
          res = await axios.get(`${apiURL}/users/${studentId}/present-days`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { month, year },
          });
        }
        const data = res.data;
        if (data?.totalDays > 0 && data?.attendancePercentage != null) {
          setProgressPercent(parseFloat(String(data.attendancePercentage).replace("%", "")) || 0);
        } else {
          setProgressPercent(0);
        }
      } catch (_) {
        setProgressPercent(0);
      }
    };
    fetchProgress();
  }, [studentId, deepLinkToken, apiURL]);

  const goToProgress = () => {
    navigate(
      { pathname: "/dashboard-student/progress", search: location.search },
      { state: { ...location.state, student, role: "student" } }
    );
  };

  const goToAttendance = () => {
    navigate(
      { pathname: "/dashboard-student/attendance", search: location.search },
      { state: { ...location.state, student, role: "student" } }
    );
  };

  const downloadProgressPdf = async () => {
    if (!studentId && !deepLinkToken) return;
    setDownloadingPdf(true);
    try {
      const now = new Date();
      const month = now.getMonth(); // 0-based for UI
      const year = now.getFullYear();
      const monthLabel = now.toLocaleString("en-US", { month: "long" });

      // Attendance summary (optional, shown in header)
      let attendanceSummary = null;
      try {
        let attRes;
        if (deepLinkToken) {
          attRes = await axios.get(
            `${apiURL}/students/by-token/${deepLinkToken}/present-days`,
            { params: { month: month + 1, year } }
          );
        } else {
          const token = localStorage.getItem("token");
          attRes = await axios.get(`${apiURL}/users/${studentId}/present-days`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { month: month + 1, year },
          });
        }
        attendanceSummary = attRes.data;
      } catch (_) {
        attendanceSummary = null;
      }

      // Progress data for whole month
      let progressData = [];
      if (deepLinkToken) {
        const res = await axios.get(`${apiURL}/students/by-token/${deepLinkToken}/progress`, {
          params: { month: month + 1, year },
        });
        progressData = res.data || [];
      } else {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${apiURL}/users/${studentId}/progress`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { month: month + 1, year },
        });
        progressData = res.data || [];
      }

      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const pdfMonth = month + 1; // 1–12 for comparison
      // Only use progress for the requested month/year (BE returns all; filter here)
      const forThisMonth = progressData.filter(
        (p) => Number(p.year) === year && Number(p.month) === pdfMonth
      );
      const makeDate = (day) =>
        `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const toDateKey = (p) => {
        if (p.date) return p.date;
        if (p.year != null && p.month != null && p.day != null)
          return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
        return null;
      };
      const byDate = new Map();
      forThisMonth.forEach((p) => {
        const key = toDateKey(p);
        if (key) byDate.set(key, p);
      });

      // Map Urdu values to English so PDF displays correctly (default font has no Urdu support)
      const urduToEnglish = (v) => {
        const map = {
          "وقت پر آئے": "Present",
          "نہیں آئے": "Absent",
          "بہت اچھا": "Good",
          "کچا پکا": "Average",
          "یاد نہیں": "No",
          "کیا ہے": "Yes",
          "نہیں کیا ہے": "No",
          "مناسب": "Appropriate",
          "مناسب نہیں ہے": "Not appropriate",
        };
        return v && map[v] != null ? map[v] : (v || "");
      };

      const rows = Array.from({ length: daysInMonth }, (_, i) => {
        const day = i + 1;
        const date = makeDate(day);
        const p = byDate.get(date) || {};
        return [
          date,
          urduToEnglish(p.attendance),
          urduToEnglish(p.sabaq),
          urduToEnglish(p.sabqi),
          urduToEnglish(p.manzil),
          p.newLessonStart || "",
          urduToEnglish(p.mutala),
          urduToEnglish(p.libaas),
        ];
      });

      const studentName = student?.name || "N/A";

      const baseUrl = `${window.location.origin}${process.env.PUBLIC_URL || ""}/assets/images`;
      const toDataUrl = async (url) => {
        try {
          const res = await fetch(url);
          if (!res.ok) return null;
          const blob = await res.blob();
          return await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
        } catch (_) {
          return null;
        }
      };

      const [logoDataUrl, codeventeLogoUrl] = await Promise.all([
        toDataUrl(`${baseUrl}/logo2.jpg`),
        toDataUrl(`${baseUrl}/codevente-logo.png`),
      ]);

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();

      const left = 40;
      const rightMargin = 40;
      const logoW = 48;
      const logoH = 48;
      const contentX = left + logoW + 16;
      const codeventeLogoW = 56;
      const codeventeLogoH = 48;
      const codeventeX = pageW - rightMargin - codeventeLogoW;

      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "JPEG", left, 22, logoW, logoH);
      }
      if (codeventeLogoUrl) {
        doc.addImage(codeventeLogoUrl, "PNG", codeventeX, 22, codeventeLogoW, codeventeLogoH);
      }

      doc.setFontSize(16);
      doc.text(`Hifz Progress Report - ${monthLabel} ${year}`, contentX, 40);
      doc.setFontSize(11);
      doc.text(`Student: ${studentName}`, contentX, 58);
      if (attendanceSummary?.attendancePercentage) {
        doc.text(
          `Attendance: ${attendanceSummary.presentDays}/${attendanceSummary.totalDays} (${attendanceSummary.attendancePercentage})`,
          contentX,
          76
        );
      }

      const tableStartY = 90;
      const urduImageCache = {};
      const getUrduImage = (text) => {
        if (!urduImageCache[text]) urduImageCache[text] = renderTextToImage(text);
        return urduImageCache[text];
      };

      autoTable(doc, {
        startY: tableStartY,
        head: [[
          "Date",
          "Attendance",
          "Sabaq",
          "Sabqi",
          "Manzil",
          "New Lesson",
          "Mutala",
          "Libaas",
        ]],
        body: rows,
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [71, 164, 71], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 247, 240] },
        columnStyles: { 0: { cellWidth: 90 }, 5: { cellWidth: 80 } },
        // Blank cells that contain Urdu/Arabic so the default font doesn't
        // draw invalid characters — we render them as images instead.
        didParseCell: (data) => {
          if (data.section === "body" && hasArabicScript(data.cell.raw)) {
            data.cell.text = [""];
          }
        },
        didDrawCell: (data) => {
          if (data.section !== "body" || !hasArabicScript(data.cell.raw)) return;
          const img = getUrduImage(data.cell.raw);
          const pad = 2;
          const maxW = data.cell.width - pad * 2;
          const maxH = data.cell.height - pad * 2;
          const ratio = Math.min(maxW / img.widthPt, maxH / img.heightPt, 1);
          const w = img.widthPt * ratio;
          const h = img.heightPt * ratio;
          const x = data.cell.x + (data.cell.width - w) / 2;
          const y = data.cell.y + (data.cell.height - h) / 2;
          doc.addImage(img.dataUrl, "PNG", x, y, w, h);
        },
      });

      const finalY = doc.lastAutoTable?.finalY ?? tableStartY;
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text("Zakaria Masjid", left, finalY + 22);
      doc.text("Powered by ", left, finalY + 38);
      const linkX = left + doc.getTextWidth("Powered by ");
      doc.textWithLink("CodeVente (codevente.com)", linkX, finalY + 38, { url: "https://codevente.com" });
      doc.setTextColor(0, 0, 0);

      doc.save(`hifz-progress-${year}-${String(month + 1).padStart(2, "0")}.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF", err);
      alert("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const goToProfile = () => {
    navigate(
      {
        pathname: "/dashboard-student/profile",
        search: location.search,
      },
      {
        state: student
          ? { student, from: "dashboard-student" }
          : { from: "dashboard-student" },
      }
    );
  };

  if (resolvingStudent) {
    return (
      <>
        <NavbarAll />
        <div className="hifz-dashboard-loading">Resolving student...</div>
      </>
    );
  }

  const studentName = student?.name || "";
  const avatarLetter = studentName ? studentName.trim().charAt(0).toUpperCase() : "?";

  return (
    <>
      <NavbarAll />
      <div className="hifz-dashboard-wrapper">
        <div className="hifz-dashboard-topbar">
          <div className="hifz-dashboard-topbar-left">
            <div className="hifz-dashboard-header-dots" aria-hidden="true">
              <span /><span /><span />
            </div>
            <h1 className="hifz-dashboard-title">Hifz Module</h1>
          </div>
          <div className="hifz-dashboard-topbar-right">
            <button
              type="button"
              className="hifz-dashboard-student hifz-dashboard-student--clickable"
              onClick={goToProfile}
              aria-label="View my profile"
            >
              <div className="hifz-dashboard-avatar" aria-hidden="true">
                {avatarLetter}
              </div>
              <span className="hifz-dashboard-student-name">
                {studentName || "Student"}
              </span>
            </button>
            {isAuthenticated && (
              <button
                type="button"
                className="hifz-logout-btn"
                onClick={handleLogout}
                aria-label="Log out"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span className="hifz-logout-btn-text">Logout</span>
              </button>
            )}
          </div>
        </div>
        <div className="hifz-dashboard">
        <div className="hifz-dashboard-cards">
          <button
            type="button"
            className="hifz-dashboard-card hifz-dashboard-card-progress"
            onClick={goToProgress}
            aria-label="Progress"
          >
            <span className="hifz-dashboard-card-title">Progress</span>
            <div className="hifz-progress-inline">
              <div className="hifz-progress-bar-wrap">
                <div
                  className="hifz-progress-bar-fill"
                  style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }}
                />
              </div>
              <span className="hifz-progress-percent">{Math.round(progressPercent)}%</span>
            </div>
            <span className="hifz-dashboard-card-icon hifz-icon-doc">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </span>
          </button>

          <button
            type="button"
            className="hifz-dashboard-card hifz-dashboard-card-profile"
            onClick={goToProfile}
            aria-label="My Profile"
          >
            <span className="hifz-dashboard-card-title">My Profile</span>
            <span className="hifz-dashboard-card-icon hifz-icon-doc">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </span>
          </button>

          <button
            type="button"
            className="hifz-dashboard-card hifz-dashboard-card-attendance"
            onClick={goToAttendance}
            aria-label="Attendance"
          >
            <span className="hifz-dashboard-card-title">Attendance</span>
            <span className="hifz-dashboard-card-icon hifz-icon-doc">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </span>
          </button>

          <button
            type="button"
            className="hifz-dashboard-card hifz-dashboard-card-download"
            onClick={downloadProgressPdf}
            aria-label="Download Progress PDF"
          >
            <span className="hifz-dashboard-card-title">
              {downloadingPdf ? "Preparing PDF..." : "Download Progress PDF"}
            </span>
            <span className="hifz-dashboard-card-icon hifz-icon-doc">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </span>
          </button>

          <a
            href="https://codevente.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hifz-dashboard-footer-card"
            aria-label="Developed by CodeVente"
          >
            <img src={`${process.env.PUBLIC_URL || ""}/assets/images/codevente-logo.png`} alt="CodeVente" className="hifz-dashboard-footer-card-logo" />
            <span className="hifz-dashboard-footer-card-text">Developed by <strong>Codevente</strong></span>
          </a>
        </div>
        </div>
      </div>
    </>
  );
}
